import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import type { CreateTrialSubscriptionRequest } from '../../types/subscription';

interface StudioDetailsFormProps {
  initialData: Partial<CreateTrialSubscriptionRequest>;
  onNext: (data: Partial<CreateTrialSubscriptionRequest>) => void;
}

export function StudioDetailsForm({ initialData, onNext }: StudioDetailsFormProps) {
  const [formData, setFormData] = useState({
    studioName: initialData.studioName || '',
    subdomain: initialData.subdomain || '',
    contactEmail: initialData.contactEmail || '',
    phone: initialData.phone || '',
    address: initialData.address || '',
    mindBodySiteId: initialData.mindBodySiteId || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.studioName.trim()) {
      newErrors.studioName = 'Studio name is required';
    }

    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Subdomain is required';
    } else if (formData.subdomain.length < 3 || formData.subdomain.length > 50) {
      newErrors.subdomain = 'Subdomain must be between 3 and 50 characters';
    } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      newErrors.subdomain = 'Subdomain can only contain lowercase letters, numbers, and hyphens';
    } else if (formData.subdomain.startsWith('-') || formData.subdomain.endsWith('-')) {
      newErrors.subdomain = 'Subdomain cannot start or end with a hyphen';
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }

    if (!formData.mindBodySiteId) {
      newErrors.mindBodySiteId = 'MindBody Site ID is required';
    } else if (isNaN(Number(formData.mindBodySiteId))) {
      newErrors.mindBodySiteId = 'Site ID must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onNext({
        studioName: formData.studioName,
        subdomain: formData.subdomain,
        contactEmail: formData.contactEmail,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        mindBodySiteId: Number(formData.mindBodySiteId),
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    // For subdomain, automatically convert to lowercase and remove spaces
    const processedValue = field === 'subdomain' ? value.toLowerCase().replace(/\s+/g, '') : value;
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <label htmlFor="studioName" className="block text-sm font-medium text-gray-700 mb-2">
            Studio Name *
          </label>
          <Input
            id="studioName"
            type="text"
            value={formData.studioName}
            onChange={(e) => handleInputChange('studioName', e.target.value)}
            placeholder="Your studio name"
            className={errors.studioName ? 'border-red-500' : ''}
          />
          {errors.studioName && (
            <p className="mt-1 text-sm text-red-600">{errors.studioName}</p>
          )}
        </div>

        <div>
          <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700 mb-2">
            Studio Subdomain *
          </label>
          <div className="flex items-center">
            <Input
              id="subdomain"
              type="text"
              value={formData.subdomain}
              onChange={(e) => handleInputChange('subdomain', e.target.value)}
              placeholder="yourcompany"
              className={`flex-1 ${errors.subdomain ? 'border-red-500' : ''}`}
            />
            <span className="ml-2 text-sm text-gray-500">.cueflow.cc</span>
          </div>
          {errors.subdomain && (
            <p className="mt-1 text-sm text-red-600">{errors.subdomain}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            This will be your studio's unique web address. Use lowercase letters, numbers, and hyphens only.
          </p>
        </div>

        <div>
          <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
            Contact Email *
          </label>
          <Input
            id="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
            placeholder="admin@yourstudio.com"
            className={errors.contactEmail ? 'border-red-500' : ''}
          />
          {errors.contactEmail && (
            <p className="mt-1 text-sm text-red-600">{errors.contactEmail}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            We'll use this email for billing and important updates
          </p>
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
          />
        </div>

        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
            Studio Address
          </label>
          <Input
            id="address"
            type="text"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="123 Main St, City, State 12345"
          />
        </div>

        <div>
          <label htmlFor="mindBodySiteId" className="block text-sm font-medium text-gray-700 mb-2">
            MindBody Site ID *
          </label>
          <Input
            id="mindBodySiteId"
            type="text"
            value={formData.mindBodySiteId}
            onChange={(e) => handleInputChange('mindBodySiteId', e.target.value)}
            placeholder="12345"
            className={errors.mindBodySiteId ? 'border-red-500' : ''}
          />
          {errors.mindBodySiteId && (
            <p className="mt-1 text-sm text-red-600">{errors.mindBodySiteId}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Find this in your MindBody business settings. We'll connect to your existing schedule and client data.
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              60-Day Free Trial
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Start with a full 60-day trial. No payment required upfront. 
                We'll send you a reminder before your trial ends.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" className="w-full sm:w-auto">
          Continue to Plan Selection
        </Button>
      </div>
    </form>
  );
}